import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FileText, Plus, CalendarRange } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { INVOICE_STATUS_META, formatCents, fmtDate } from "./billingMeta";
import { InvoiceFormModal } from "./InvoiceFormModal";
import { InvoiceDetailDrawer } from "./InvoiceDetailDrawer";
import { PaymentScheduleFormModal } from "./PaymentScheduleFormModal";
import { PaymentScheduleDetailDrawer } from "./PaymentScheduleDetailDrawer";

const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC]";

export const BillingInvoicesTab = ({ API, canManage, focusInvoiceId, clearFocus, autoOpenCreate, clearAutoOpenCreate }) => {
  const [invoices, setInvoices] = useState(null);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [openInvoiceId, setOpenInvoiceId] = useState(focusInvoiceId || null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [schedules, setSchedules] = useState(null);
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);
  const [openScheduleId, setOpenScheduleId] = useState(null);

  const loadSchedules = useCallback(() => {
    axios.get(`${API}/org/billing/payment-schedules`, { withCredentials: true }).then(({ data }) => setSchedules(data.schedules)).catch(() => {});
  }, [API]);
  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const load = useCallback(() => {
    axios.get(`${API}/org/billing/invoices`, { params: { status: statusFilter || undefined, search: search || undefined }, withCredentials: true })
      .then(({ data }) => setInvoices(data.invoices))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, statusFilter, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (focusInvoiceId) { setOpenInvoiceId(focusInvoiceId); clearFocus(); } }, [focusInvoiceId, clearFocus]);
  useEffect(() => { if (autoOpenCreate) { setShowCreate(true); clearAutoOpenCreate(); } }, [autoOpenCreate, clearAutoOpenCreate]);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="billing-invoices-error">{error}</p>;

  return (
    <div data-testid="billing-invoices-tab">
      <Panel
        title="Payment Schedules"
        subtitle="Deposits, milestones and final balances for Work Jobs."
        action={canManage && <button type="button" onClick={() => setShowCreateSchedule(true)} data-testid="billing-schedules-create-btn" className={btn}><Plus size={14} /> New Payment Schedule</button>}
      >
        {!schedules ? (
          <p className="text-sm text-slate-400" data-testid="billing-schedules-loading">Loading…</p>
        ) : schedules.length === 0 ? (
          <EmptyState icon={CalendarRange} title="No Payment Schedules yet." message="Create a deposit + milestone schedule for a Work Job's accepted Proposal." testId="billing-schedules-empty" />
        ) : (
          <div className="space-y-2" data-testid="billing-schedules-list">
            {schedules.map((s) => {
              const billed = s.milestones.filter((m) => m.effective_status === "Invoiced").length;
              return (
                <button key={s.schedule_id} type="button" onClick={() => setOpenScheduleId(s.schedule_id)} data-testid={`billing-schedule-row-${s.schedule_id}`} className="flex w-full items-center justify-between rounded-xl border border-slate-200 p-3 text-left transition-colors duration-150 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5">
                  <div><p className="text-sm font-bold">{formatCents(s.authoritative_total_cents)} total</p><p className="text-xs text-slate-500 dark:text-slate-400">{s.source === "accepted_proposal" ? "Reconciled to accepted Proposal" : "Standalone"} · {billed} of {s.milestones.length} milestones invoiced</p></div>
                </button>
              );
            })}
          </div>
        )}
      </Panel>

      <Panel
        title="Invoices"
        subtitle="Every customer invoice your organization has created."
        action={canManage && <button type="button" onClick={() => setShowCreate(true)} data-testid="billing-invoices-create-btn" className={btn}><Plus size={14} /> Create Invoice</button>}
      >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="billing-invoices-status-filter" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Statuses</option>
            {Object.keys(INVOICE_STATUS_META).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search invoice # or customer…" data-testid="billing-invoices-search-input" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
        </div>

        {!invoices ? (
          <p className="text-sm text-slate-400" data-testid="billing-invoices-loading">Loading…</p>
        ) : invoices.length === 0 ? (
          <EmptyState icon={FileText} title="No invoices yet." message={canManage ? "Create your first invoice to start tracking what customers owe." : "Invoices created for your Team will show up here."} testId="billing-invoices-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="billing-invoices-table">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wide text-slate-400"><th className="pb-2">Invoice #</th><th className="pb-2">Customer</th><th className="pb-2">Related Job</th><th className="pb-2">Amount</th><th className="pb-2">Status</th><th className="pb-2">Due Date</th></tr></thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50 dark:border-white/5 dark:hover:bg-white/5" onClick={() => setOpenInvoiceId(inv.invoice_id)} data-testid={`billing-invoice-row-${inv.invoice_id}`}>
                    <td className="py-2.5 font-bold text-[#0052FF]">{inv.display_id}</td>
                    <td className="py-2.5">{inv.customer_snapshot.first_name} {inv.customer_snapshot.last_name}</td>
                    <td className="py-2.5 text-slate-500 dark:text-slate-400">{inv.work_job_id || "—"}</td>
                    <td className="py-2.5 font-semibold">{formatCents(inv.total_cents)}</td>
                    <td className="py-2.5"><span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${INVOICE_STATUS_META[inv.effective_status].cls}`}>{INVOICE_STATUS_META[inv.effective_status].label}</span></td>
                    <td className="py-2.5 text-slate-500 dark:text-slate-400">{fmtDate(inv.due_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {showCreate && <InvoiceFormModal API={API} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editingInvoice && <InvoiceFormModal API={API} invoice={editingInvoice} onClose={() => setEditingInvoice(null)} onSaved={() => { setEditingInvoice(null); load(); }} />}
      {openInvoiceId && !editingInvoice && (
        <InvoiceDetailDrawer API={API} invoiceId={openInvoiceId} canManage={canManage} onClose={() => setOpenInvoiceId(null)} onChanged={() => { load(); loadSchedules(); }} onEdit={(inv) => { setOpenInvoiceId(null); setEditingInvoice(inv); }} />
      )}
      {showCreateSchedule && <PaymentScheduleFormModal API={API} onClose={() => setShowCreateSchedule(false)} onSaved={() => { setShowCreateSchedule(false); loadSchedules(); }} />}
      {openScheduleId && !openInvoiceId && (
        <PaymentScheduleDetailDrawer API={API} scheduleId={openScheduleId} canManage={canManage} onClose={() => { setOpenScheduleId(null); loadSchedules(); }} onOpenInvoice={(invId) => { setOpenScheduleId(null); setOpenInvoiceId(invId); }} />
      )}
    </div>
  );
};
